import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList } from 'recharts';
import { Users, Music, FileText, CreditCard, HelpCircle, AlertTriangle, TrendingUp, Wallet, Clock, DollarSign } from 'lucide-react';

const fmtEur = v => v > 0 ? `€${parseFloat(v).toFixed(0)}` : '';

export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revenueYear, setRevenueYear] = useState(new Date().getFullYear());
  const [fetchingChart, setFetchingChart] = useState(false);

  // Top lists — stato separato
  const [topData, setTopData] = useState({ top_streams: [], top_revenue: [] });
  const [topPeriod, setTopPeriod] = useState('all');
  const [topType, setTopType] = useState('artists');
  const [fetchingTop, setFetchingTop] = useState(false);

  // Carica grafico + overview (solo quando cambia anno)
  useEffect(() => {
    (async () => {
      setFetchingChart(true);
      try {
        const { data } = await api.get(`/admin/stats?year=${revenueYear}`);
        setStats(data);
      } catch (err) { toast.error('Errore caricamento'); }
      finally { setLoading(false); setFetchingChart(false); }
    })();
  }, [revenueYear]);

  // Carica top lists (solo quando cambia periodo o tipo)
  const loadTop = useCallback(async () => {
    setFetchingTop(true);
    try {
      const { data } = await api.get(`/admin/stats/top?period=${topPeriod}&type=${topType}`);
      setTopData(data);
    } catch (e) {}
    finally { setFetchingTop(false); }
  }, [topPeriod, topType]);

  useEffect(() => { loadTop(); }, [loadTop]);

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-yellow-400" /></div>;

  const o = stats?.overview || {};
  const years = stats?.available_years?.length > 0 ? stats.available_years : [new Date().getFullYear()];

  // Totali anno per il riepilogo sotto il grafico
  const rev = stats?.monthly_revenue || [];
  const totals = {
    pn: rev.reduce((s, m) => s + (m.pn_share || 0), 0),
    user: rev.reduce((s, m) => s + (m.user_share || 0), 0),
    ytPn: rev.reduce((s, m) => s + (m.yt_pn_share || 0), 0),
    ytUser: rev.reduce((s, m) => s + (m.yt_user_share || 0), 0),
  };

  const cards = [
    { label: 'Utenti Attivi', value: o.active_users, icon: Users, color: 'text-green-400', link: '/admin/users' },
    { label: 'In Attesa Attivazione', value: o.pending_users, icon: Clock, color: 'text-yellow-400', link: '/admin/users', alert: o.pending_users > 0 },
    { label: 'Release Totali', value: o.total_releases, icon: Music, color: 'text-blue-400', link: '/admin/releases' },
    { label: 'Da Revisionare', value: o.pending_releases, icon: AlertTriangle, color: 'text-orange-400', link: '/admin/releases', alert: o.pending_releases > 0 },
    { label: 'Live', value: o.distributed_releases, icon: TrendingUp, color: 'text-emerald-400' },
    { label: 'Ticket Aperti', value: o.open_tickets, icon: HelpCircle, color: 'text-purple-400', link: '/admin/support', alert: o.open_tickets > 0 },
    { label: 'Saldo Da Pagare', value: `€${parseFloat(o.total_balance_owed || 0).toFixed(2)}`, icon: Wallet, color: 'text-red-400', link: '/admin/payments' },
    { label: 'Pagamenti In Attesa', value: o.pending_payments, icon: CreditCard, color: 'text-pink-400', link: '/admin/payments', alert: o.pending_payments > 0 },
  ];

  const TopList = ({ data, valueKey, formatValue }) => (
    data?.length > 0 ? (
      <div className="space-y-3">
        {data.map((a, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-gray-700 text-gray-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{a.name || a.artist_name}</p>
              <div className="h-1.5 bg-gray-700 rounded-full mt-1">
                <div className="h-1.5 bg-pn-red rounded-full" style={{ width: `${(parseFloat(a[valueKey]) / parseFloat(data[0][valueKey]) * 100)}%` }} />
              </div>
            </div>
            <span className="text-xs text-gray-400">{formatValue(a[valueKey])}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="flex items-center justify-center h-48 text-gray-500 text-sm">Nessun dato</div>
    )
  );

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard Admin</h1>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className={`card-dark p-4 ${c.link ? 'hover:border-gray-600 transition cursor-pointer' : ''} relative`}
            onClick={() => c.link && (window.location.href = c.link)}>
            {c.alert && <div className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />}
            <div className="flex items-center gap-2 mb-2">
              <c.icon size={16} className={c.color} />
              <span className="text-xs text-gray-400">{c.label}</span>
            </div>
            <p className="text-2xl font-bold text-white">{c.value || 0}</p>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      <div className="card-dark p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-white">Revenue Mensile</h2>
          <div className="flex items-center gap-2">
            {fetchingChart && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400" />}
            <select className="bg-gray-800 text-white border border-gray-600 rounded-lg px-3 py-1.5 text-sm focus:border-yellow-500 outline-none"
              value={revenueYear} onChange={e => setRevenueYear(parseInt(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        {rev.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={rev} margin={{ top: 20, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="period_month" tick={{ fill: '#888', fontSize: 11 }}
                  tickFormatter={m => ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'][m-1]} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} tickFormatter={v => `€${v}`} />
                <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }}
                  labelFormatter={m => {
                    const mesi = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
                    return `${mesi[m-1]} ${revenueYear}`;
                  }}
                  formatter={(v, name) => [`€${parseFloat(v).toFixed(2)}`, name]} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Bar dataKey="pn_share" name="Royalties PN" fill="#E94560" radius={[4,4,0,0]}>
                  <LabelList dataKey="pn_share" position="top" fill="#E94560" fontSize={9} formatter={fmtEur} />
                </Bar>
                <Bar dataKey="user_share" name="Royalties Artisti" fill="#0F3460" radius={[4,4,0,0]}>
                  <LabelList dataKey="user_share" position="top" fill="#6B8CC7" fontSize={9} formatter={fmtEur} />
                </Bar>
                <Bar dataKey="yt_pn_share" name="YouTube PN" fill="#FF6B00" radius={[4,4,0,0]}>
                  <LabelList dataKey="yt_pn_share" position="top" fill="#FF6B00" fontSize={9} formatter={fmtEur} />
                </Bar>
                <Bar dataKey="yt_user_share" name="YouTube Artisti" fill="#22C55E" radius={[4,4,0,0]}>
                  <LabelList dataKey="yt_user_share" position="top" fill="#22C55E" fontSize={9} formatter={fmtEur} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {/* Riepilogo annuale */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-700">
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">Royalties PN</p>
                <p className="text-lg font-bold text-[#E94560]">€{totals.pn.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">Royalties Artisti</p>
                <p className="text-lg font-bold text-[#6B8CC7]">€{totals.user.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">YouTube PN</p>
                <p className="text-lg font-bold text-[#FF6B00]">€{totals.ytPn.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-gray-500 uppercase">YouTube Artisti</p>
                <p className="text-lg font-bold text-[#22C55E]">€{totals.ytUser.toFixed(2)}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-48 text-gray-500 text-sm">Nessun dato revenue per il {revenueYear}</div>
        )}
      </div>

      {/* Top Lists */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-bold text-white text-lg">Top 10</h2>
        <div className="flex gap-2 flex-wrap">
          {/* Tipo: Artisti vs Brani */}
          {[{ v: 'artists', l: '🎤 Artisti' }, { v: 'tracks', l: '🎵 Brani' }].map(t => (
            <button key={t.v} onClick={() => setTopType(t.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${topType === t.v ? 'bg-pn-red/20 text-pn-red border border-pn-red/30' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>
              {t.l}
            </button>
          ))}
          <div className="w-px bg-gray-700 mx-1" />
          {/* Periodo */}
          {[{ v: 'all', l: 'Totale' }, { v: '3m', l: 'Ultimi 3 mesi' }].map(p => (
            <button key={p.v} onClick={() => setTopPeriod(p.v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${topPeriod === p.v ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>
              {p.l}
            </button>
          ))}
          {fetchingTop && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400 self-center" />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card-dark p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-blue-400" />
            <h3 className="font-bold text-white">Per Stream</h3>
          </div>
          <TopList data={topData?.top_streams} valueKey="total_streams"
            formatValue={v => parseInt(v).toLocaleString()} />
        </div>
        <div className="card-dark p-6">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={16} className="text-green-400" />
            <h3 className="font-bold text-white">Per Guadagno</h3>
          </div>
          <TopList data={topData?.top_revenue} valueKey="total_revenue"
            formatValue={v => `€${parseFloat(v).toFixed(2)}`} />
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Link to="/admin/releases" className="card-dark p-4 hover:border-yellow-500/50 transition text-center">
          <Music size={20} className="mx-auto mb-2 text-yellow-400" />
          <span className="text-sm text-gray-300">Revisiona Release</span>
          {o.pending_releases > 0 && <span className="block text-xs text-yellow-400 font-bold mt-1">{o.pending_releases} in attesa</span>}
        </Link>
        <Link to="/admin/statements" className="card-dark p-4 hover:border-yellow-500/50 transition text-center">
          <FileText size={20} className="mx-auto mb-2 text-blue-400" />
          <span className="text-sm text-gray-300">Carica Rendiconti</span>
        </Link>
        <Link to="/admin/users" className="card-dark p-4 hover:border-yellow-500/50 transition text-center">
          <Users size={20} className="mx-auto mb-2 text-green-400" />
          <span className="text-sm text-gray-300">Gestisci Utenti</span>
          {o.pending_users > 0 && <span className="block text-xs text-green-400 font-bold mt-1">{o.pending_users} da attivare</span>}
        </Link>
        <Link to="/admin/payments" className="card-dark p-4 hover:border-yellow-500/50 transition text-center">
          <CreditCard size={20} className="mx-auto mb-2 text-pink-400" />
          <span className="text-sm text-gray-300">Pagamenti</span>
          {o.pending_payments > 0 && <span className="block text-xs text-pink-400 font-bold mt-1">{o.pending_payments} richieste</span>}
        </Link>
      </div>
    </div>
  );
}
